import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import productService from "../../services/productService";

// Get backend URL from environment variables
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:5001";

/**
 * Product Image Manager component for admin panel
 * Allows uploading, editing, deleting, and reordering product images
 * @param {Object} props - Component props
 * @param {string} props.productId - Product ID
 * @param {Function} props.onImagesChange - Callback when images change
 * @returns {JSX.Element} ProductImageManager component
 */
const ProductImageManager = ({ productId, onImagesChange }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    alt: "",
    caption: "",
    isPrimary: false,
  });
  const fileInputRef = useRef(null);

  // Fetch product images on component mount
  useEffect(() => {
    if (productId) {
      fetchImages();
    }
  }, [productId]);

  // Fetch product images
  const fetchImages = async () => {
    try {
      setLoading(true);
      const data = await productService.getProductImages(productId);
      setImages(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching product images:", err);
      setError(t("Failed to load product images"));
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      uploadImages(files);
    }
  };

  // Upload images
  const uploadImages = async (files) => {
    try {
      setUploading(true);
      setError(null);

      // Validate file types
      const validFiles = files.filter((file) =>
        ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
          file.type
        )
      );

      if (validFiles.length === 0) {
        setError(t("Please select valid image files (JPG, PNG, WebP)"));
        setUploading(false);
        return;
      }

      // Create form data
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("images", file);
      });

      // Upload images
      const result = await productService.uploadProductImages(
        productId,
        formData
      );

      // Refresh images
      await fetchImages();

      // Show success message
      setSuccess(
        t("{{count}} images uploaded successfully", {
          count: validFiles.length,
        })
      );

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Notify parent component
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (err) {
      console.error("Error uploading images:", err);
      setError(t("Failed to upload images"));
    } finally {
      setUploading(false);
    }
  };

  // Handle image edit
  const handleEditImage = (image) => {
    setCurrentImage(image);
    setEditFormData({
      alt: image.alt || "",
      caption: image.caption || "",
      isPrimary: image.isPrimary || false,
    });
    setEditDialogOpen(true);
  };

  // Handle edit form change
  const handleEditFormChange = (event) => {
    const { name, value, checked } = event.target;
    setEditFormData({
      ...editFormData,
      [name]: name === "isPrimary" ? checked : value,
    });
  };

  // Save image edit
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError(null);

      await productService.updateProductImage(
        productId,
        currentImage.id,
        editFormData
      );

      // If setting as primary, update primary image
      if (editFormData.isPrimary && !currentImage.isPrimary) {
        await productService.setPrimaryImage(productId, currentImage.id);
      }

      // Refresh images
      await fetchImages();

      // Show success message
      setSuccess(t("Image updated successfully"));

      // Close dialog
      setEditDialogOpen(false);

      // Notify parent component
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (err) {
      console.error("Error updating image:", err);
      setError(t("Failed to update image"));
    } finally {
      setLoading(false);
    }
  };

  // Handle image delete
  const handleDeleteImage = async (imageId) => {
    if (window.confirm(t("Are you sure you want to delete this image?"))) {
      try {
        setLoading(true);
        setError(null);

        await productService.deleteProductImage(productId, imageId);

        // Refresh images
        await fetchImages();

        // Show success message
        setSuccess(t("Image deleted successfully"));

        // Notify parent component
        if (onImagesChange) {
          onImagesChange();
        }
      } catch (err) {
        console.error("Error deleting image:", err);
        setError(t("Failed to delete image"));
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle setting primary image
  const handleSetPrimary = async (imageId) => {
    try {
      setLoading(true);
      setError(null);

      await productService.setPrimaryImage(productId, imageId);

      // Refresh images
      await fetchImages();

      // Show success message
      setSuccess(t("Primary image updated"));

      // Notify parent component
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (err) {
      console.error("Error setting primary image:", err);
      setError(t("Failed to set primary image"));
    } finally {
      setLoading(false);
    }
  };

  // Handle drag end (reordering)
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for better UX
    setImages(items);

    try {
      // Send reorder request to API
      const imageOrder = items.map((item) => item.id);
      await productService.reorderProductImages(productId, imageOrder);

      // Show success message
      setSuccess(t("Images reordered successfully"));

      // Notify parent component
      if (onImagesChange) {
        onImagesChange();
      }
    } catch (err) {
      console.error("Error reordering images:", err);
      setError(t("Failed to reorder images"));
      // Revert to original order on error
      fetchImages();
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Helper function to get direct URL to image
  const getDirectImageUrl = (imageUrl) => {
    if (!imageUrl) return "";

    // If it's already an absolute URL (like S3), return as is
    if (imageUrl.startsWith("http")) {
      return imageUrl;
    }

    // For local storage, replace the base URL with our direct backend URL
    // The imageUrl will be something like "http://localhost:5001/uploads/productId/filename.png"
    const urlParts = imageUrl.split("/uploads/");
    if (urlParts.length === 2) {
      return `${BACKEND_URL}/uploads/${urlParts[1]}`;
    }

    return imageUrl;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t("Product Images")}
      </Typography>

      {/* Upload Button */}
      <Box sx={{ mb: 2 }}>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              {t("Uploading...")}
            </>
          ) : (
            t("Upload Images")
          )}
        </Button>
      </Box>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Images Grid */}
      {loading && !uploading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : images.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("No images uploaded yet")}
          </Typography>
        </Paper>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="images" direction="horizontal">
            {(provided) => (
              <Grid
                container
                spacing={2}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {images.map((image, index) => (
                  <Draggable
                    key={image.id}
                    draggableId={image.id}
                    index={index}
                  >
                    {(provided) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1,
                            position: "relative",
                            border: image.isPrimary ? "2px solid" : "1px solid",
                            borderColor: image.isPrimary
                              ? "primary.main"
                              : "divider",
                          }}
                        >
                          {/* Drag Handle */}
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              position: "absolute",
                              top: 5,
                              left: 5,
                              zIndex: 1,
                              cursor: "grab",
                              bgcolor: "rgba(255,255,255,0.7)",
                              borderRadius: "50%",
                              p: 0.5,
                            }}
                          >
                            <DragIcon />
                          </Box>

                          {/* Image */}
                          <Box
                            sx={{
                              height: 200,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              component="img"
                              src={getDirectImageUrl(image.imageUrl)}
                              alt={image.alt || "Product image"}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </Box>

                          {/* Image Info */}
                          <Box sx={{ mt: 1 }}>
                            {image.alt && (
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {image.alt}
                              </Typography>
                            )}
                          </Box>

                          {/* Actions */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          >
                            <Tooltip title={t("Edit image details")}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditImage(image)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip
                              title={
                                image.isPrimary
                                  ? t("Primary image")
                                  : t("Set as primary")
                              }
                            >
                              {image.isPrimary ? (
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={true}
                                  >
                                    <StarIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              ) : (
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={() => handleSetPrimary(image.id)}
                                >
                                  <StarBorderIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Tooltip>

                            <Tooltip title={t("Delete image")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteImage(image.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Edit Image Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Edit Image Details")}</DialogTitle>
        <DialogContent>
          {currentImage && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                  mt: 1,
                }}
              >
                <Box
                  component="img"
                  src={getDirectImageUrl(currentImage.imageUrl)}
                  alt={currentImage.alt || "Product image"}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                  }}
                />
              </Box>

              <TextField
                fullWidth
                margin="dense"
                label={t("Alt Text")}
                name="alt"
                value={editFormData.alt}
                onChange={handleEditFormChange}
                helperText={t("Description of the image for accessibility")}
              />

              <TextField
                fullWidth
                margin="dense"
                label={t("Caption")}
                name="caption"
                value={editFormData.caption}
                onChange={handleEditFormChange}
                helperText={t("Optional caption to display with the image")}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.isPrimary}
                    onChange={handleEditFormChange}
                    name="isPrimary"
                    disabled={currentImage.isPrimary}
                  />
                }
                label={t("Set as primary image")}
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ mr: 1 }} />
            ) : (
              t("Save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductImageManager;
